import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { Cable } from '@services/cable';
import { TokenService } from '@services/token-service';
import { GlobalID } from '@utils/global-id';

import * as ActionCable from '@rails/actioncable';
import { DashboardSessionDetails, SessionsService } from '@services/sessions.service';
import { Notifier } from '@services/notifier';

export interface ClaimsListChannelData {
  started?: boolean;
  claimed?: boolean;
  finished?: boolean;
  id?: string;
  claimedBy?: string;
  claimedById: number;
}

@Directive()
export abstract class AbstractClaimsListDirective implements OnInit, OnDestroy {
  private readonly cable = inject(Cable);
  private readonly tokenService = inject(TokenService);

  protected readonly notifier = inject(Notifier);
  protected readonly sessionsService = inject(SessionsService);

  currentUserId: number;
  sessionChannel: ActionCable.Channel;

  ngOnInit() {
    this.notifier.requestPermission();

    this.currentUserId = this.tokenService.currentUserData.id;

    this.bindToCable();
  }

  ngOnDestroy() {
    this.sessionChannel.unsubscribe();
  }

  protected filterMySessions(sessions: DashboardSessionDetails[]) {
    return sessions.filter((session) => {
      if (!session.claimedBy) {
        return true;
      }

      return GlobalID.decode(session.claimedBy.id).id === this.currentUserId;
    });
  }

  private bindToCable() {
    this.sessionChannel = this.cable.create('ClaimsListChannel', (rawData: ClaimsListChannelData) => {
      const parsedData = this.parseChannelMessage(rawData);

      this.handleChannelData(parsedData);
    });
  }

  /**
   * Parses the raw data from the cable channel.
   * Also guess the numeric user id from the global id string.
   */
  private parseChannelMessage(data: ClaimsListChannelData) {
    const { claimedBy } = data;

    if (!claimedBy) {
      return data;
    }

    const parsedId = GlobalID.decode(claimedBy);

    return { claimedById: parsedId.id, ...data };
  }

  protected handleChannelData(parsedData: ClaimsListChannelData) {
    /// If claimed by somebody else, or finished, let's remove from the list
    if ((parsedData.claimed && parsedData.claimedById !== this.currentUserId) || parsedData.finished) {
      this.sessionsService.emitReloadTrigger();
    }
  }
}
